import { type ComponentProps, forwardRef, type MouseEvent, memo, useCallback } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentMentionFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionFilterChip';
import { workspaceAgentMentionFilterChip } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMentionFilterChipChrome';
import { useWorkspaceAgentSelectAssetStore } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';

export type WorkspaceAgentSelectAssetBoardFilterChipProps = ComponentProps<'span'>;

export const WorkspaceAgentSelectAssetBoardFilterChip = memo(
  forwardRef<HTMLSpanElement, WorkspaceAgentSelectAssetBoardFilterChipProps>(
    function WorkspaceAgentSelectAssetBoardFilterChip({ className, ...rest }, ref) {
      const isActive = useWorkspaceAgentSelectAssetStore(
        (state) => state.source === 'board' || state.isBoardPickerOpen,
      );
      const chooseSource = useWorkspaceAgentSelectAssetStore((state) => state.chooseSource);
      const openBoardPicker = useWorkspaceAgentSelectAssetStore((state) => state.openBoardPicker);
      const handleCaretMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
      }, []);

      const handleCaretClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          openBoardPicker();
        },
        [openBoardPicker],
      );

      const handleSelect = useCallback(() => {
        chooseSource('board');
      }, [chooseSource]);

      return (
        <span
          {...rest}
          className={cn('inline-flex min-w-0 shrink-0 items-center', className)}
          data-testid="WORKSPACE_AGENT_SELECT_ASSET_BOARD_CHIP"
          ref={ref}
        >
          <WorkspaceAgentMentionFilterChip
            className="rounded-r-none border-r-0"
            isActive={isActive}
            label="Board"
            onSelect={handleSelect}
            sourceKey="boards"
          />
          <button
            aria-label="Choose board"
            className={workspaceAgentMentionFilterChip({ isCaret: true })}
            data-testid="WORKSPACE_AGENT_SELECT_ASSET_BOARD_CHIP_CARET"
            onClick={handleCaretClick}
            onMouseDown={handleCaretMouseDown}
            type="button"
          >
            <ChevronDownIcon aria-hidden size="xs" />
          </button>
        </span>
      );
    },
  ),
);

WorkspaceAgentSelectAssetBoardFilterChip.displayName = 'WorkspaceAgentSelectAssetBoardFilterChip';
